import { Div, Button, router } from '@codesuma/baseline'
import tasks, { ITask } from '../../../services/tasks'
import calendar from '../../../services/calendar'
import rating from '../../../services/rating'
import { toFaDigits } from '../../../helpers/digits'
import { RowMenu, RowMenuButton, closeRowMenu, IRowMenuItem } from './row-menu'
import style from './index.module.css'

// One task in the day's list: a ring to tick it off, the title, the hour it is
// due if it has one, and a bell if it is going to be announced.
//
// Two taps and that is the whole row. The ring marks it done; the title opens
// the task page on itself, which is where the name, the hour, the reminder and
// the delete all live. Nothing is edited in the row — the wizard that makes a
// task is the one that changes it, so there is only ever one place to look.

// Material Design icons (Apache 2.0), inheriting currentColor.
const CHECK_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>'
// What a task's dots offer. Putting it off is two answers rather than one
// because "tomorrow" and "not for a while" are different intentions.
const OPTIONS: IRowMenuItem[] = [
    { action: 'done', label: 'انجام شد' },
    { action: 'tomorrow', label: 'به فردا بیفکن' },
    { action: 'later', label: 'چند روز دیگر' },
    { action: 'edit', label: 'ویرایش' },
    { action: 'remove', label: 'حذف', danger: true },
]
const BELL_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>'

export const TaskRow = (task: ITask) => {
    const base = Div()
    base.addClass(style.task)

    const row = Div()
    row.addClass(style.taskRow)
    base.append(row)

    // Done. The button is the whole of the chevron's column — see .tick —
    // with the ring drawn in the middle of it, where the chevron's mark sits.
    const tick = Button()
    tick.addClass(style.tick)
    const ring = Div()
    ring.addClass(style.tickRing)
    ring.html(CHECK_SVG)
    tick.append(ring)
    const syncDone = () => {
        base.toggleClass(style.taskDone, task.done)
        tick.attr('aria-pressed', String(task.done))
        tick.attr('aria-label', task.done ? 'انجام‌نشده' : 'انجام شد')
    }
    tick.on('click', () => {
        task.done = !task.done
        tasks.update(task.id, { done: task.done })
        syncDone()
        if (task.done) rating.goodMoment()
    })
    syncDone()
    row.append(tick)

    // The title opens the task page on this task, the way a saved occasion
    // opens the occasion page on itself.
    const title = Button(task.title)
    title.addClass(style.taskTitle)
    title.attr('aria-label', `ویرایش ${task.title}`)
    title.on('click', () => router.goto(`/task?id=${task.id}`))
    row.append(title)

    // The hour, when the task has one. Without it a time set in the wizard
    // would be invisible everywhere but the notification it produces.
    if (task.time) {
        const time = Div(toFaDigits(task.time))
        time.addClass(style.taskTime)
        row.append(time)
    }

    // A bell rather than words: the row is one line of text and the reminder is
    // an aside on it. How long before belongs to the page that set it.
    if (task.reminder !== 'none') {
        const bell = Div()
        bell.addClass(style.taskBell)
        bell.html(BELL_SVG)
        bell.attr('role', 'img')
        bell.attr('aria-label', 'یادآوری دارد')
        row.append(bell)
    }

    // Everything else the row can do, on the far side of it. The ring and the
    // title are the two taps worth having in the row itself; the rest are
    // reached for rarely enough to be worth one more press.
    //
    // Putting a task off moves it to another day, so this row stops belonging
    // to the list it is in — hence 'changed', which has the list redraw. Ticking
    // it off does not: the row says so itself, and redrawing would take the
    // list out from under the reader's finger.
    const more = RowMenuButton(`گزینه‌های ${task.title}`)
    const put = (days: number) =>
        tasks.moveTo(task.id, calendar.dayAt(task.gy, task.gm, task.gd, days))
    more.on('click', () => {
        const menu = RowMenu(more.el, OPTIONS)
        menu.on('pick', (action: string) => {
            switch (action) {
                case 'done':
                    task.done = true
                    tasks.update(task.id, { done: true })
                    syncDone()
                    rating.goodMoment()
                    return
                case 'tomorrow': put(1); break
                case 'later': put(3); break
                case 'edit': return router.goto(`/task?id=${task.id}`)
                case 'remove': tasks.remove(task.id); break
            }
            base.emit('changed')
        })
    })
    // The menu is hung off the body, so it has to be put away with the row
    // rather than left behind when the slide redraws.
    base.on('unmounted', closeRowMenu)
    row.append(more)

    return base
}
